"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Menu, X } from "lucide-react";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="16757" className="hidden lg:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-24 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-xs font-bold font-serif uppercase text-foreground/80 transition-colors hover:text-primary focus:text-primary outline-none tracking-widest">
                {item.label}
                <span className="absolute inset-x-0 bottom-0 h-0.5 w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block rounded-none px-4 py-4 text-lg font-bold font-serif uppercase tracking-tight text-foreground hover:bg-accent hover:text-primary transition-colors border-l-4 border-transparent hover:border-primary" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function PrimaryNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/90 backdrop-blur-md border-border/40 shadow-md shadow-primary/5" : "bg-background"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center gap-2 focus:outline-none">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597043/img/competition_engine_machine_logo.png" alt="BRENDON J BUILDING MATERIAL WHOLESALERS PTY LTD" className="h-7 w-auto object-contain brightness-0 dark:brightness-100" />
          </Link>
        </div>

        <div className="hidden lg:flex flex-1 justify-center px-4">
          <MenuProvider menu_id="44" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-3">
          <Button asChild className="hidden sm:flex bg-primary text-primary-foreground hover:bg-primary/90 font-bold font-serif uppercase tracking-widest rounded-none shadow-sm hover:shadow-md transition-all px-6">
            <Link to="/ContactPage">
              Request a Quote
            </Link>
          </Button>
          
          <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:text-primary hover:bg-accent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background/98 backdrop-blur-xl border-b border-border shadow-2xl lg:hidden animate-in slide-in-from-top-5 duration-200">
          <div className="container mx-auto py-4">
            <MenuProvider menu_id="44">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="p-4 border-t border-border mt-2 flex flex-col gap-4">
              <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none py-7 text-lg font-bold font-serif uppercase tracking-widest">
                <Link to="/ContactPage" onClick={() => setIsMobileMenuOpen(false)}>
                  Request a Quote
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}